/**
 * DcVideoStatsSection — подвкладка «Видео» (DC → Юнифанс). База моментов, статистика
 * просмотров (показы, время, задержка после игры), топ-видео с плеером, часто смотрящие,
 * полная библиотека всех видео с сортировкой (квест/дата/оператор). Данные — useVideoStats.
 * Стиль — конституция DC: var(--*), цифры данных = var(--f-meta) tabular-nums (C-цифры),
 * плотность, HUD. Период: по умолчанию 1 июля 2026 → сегодня; календарь в углу пересчитывает.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { useVideoStats, type VideoTopItem, type VideoTopViewer } from "../../hooks/useVideoStats";
import { AvatarImg, Bar, Kpi, NUM, VideoModal, VideoThumb, box, capTitle, fmt, fmtDm, fmtWhen, todayBiz } from "./dcVideoStatsParts";

const JULY_START = "2026-07-01";

export function DcVideoStatsSection({ from, to, onImpersonate, onSetRange }: {
  onToast?: (m: string) => void; from?: string; to?: string; onImpersonate?: (tg: number) => void; onSetRange?: (from: string, to: string) => void;
}): ReactElement {
  // По умолчанию период 1 июля 2026 → сегодня; на первом открытии сеем угловой календарь.
  useEffect(() => { onSetRange?.(JULY_START, todayBiz()); /* eslint-disable-next-line */ }, []);
  const effFrom = from || JULY_START;
  const effTo = to || todayBiz();
  const { data, loading, error, reload } = useVideoStats(effFrom, effTo);
  const [tab, setTab] = useState<"videos" | "viewers" | "library">("videos");
  const [libSort, setLibSort] = useState<"date" | "quest" | "operator" | "game">("date");
  const [openVideo, setOpenVideo] = useState<VideoTopItem | null>(null);

  const wrap: CSSProperties = { display: "grid", gap: 8, padding: 10, paddingBottom: "calc(12px + var(--safe-bottom))" };
  const seg = (active: boolean): CSSProperties => ({ flex: 1, padding: "4px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid " + (active ? "var(--purple)" : "var(--surface-3)"), background: active ? "color-mix(in srgb, var(--purple) 18%, transparent)" : "transparent", color: active ? "var(--text-1)" : "var(--text-2)" });

  const library = useMemo(() => {
    const rows = data?.library ? [...data.library] : [];
    if (libSort === "quest") rows.sort((a, b) => a.quest.localeCompare(b.quest, "ru") || b.gameDate.localeCompare(a.gameDate));
    else if (libSort === "operator") rows.sort((a, b) => (a.operator || "я").localeCompare(b.operator || "я", "ru") || b.gameDate.localeCompare(a.gameDate));
    else if (libSort === "game") rows.sort((a, b) => b.gameDate.localeCompare(a.gameDate) || b.gameTime.localeCompare(a.gameTime) || a.bookingId.localeCompare(b.bookingId, undefined, { numeric: true }) || a.materialId.localeCompare(b.materialId, undefined, { numeric: true }));
    else rows.sort((a, b) => b.gameDate.localeCompare(a.gameDate) || b.gameTime.localeCompare(a.gameTime));
    return rows;
  }, [data?.library, libSort]);

  if (loading && !data) return <div style={wrap}><div style={{ ...box, padding: 14, color: "var(--text-2)", fontSize: 12 }}>Загрузка статистики…</div></div>;
  if (error) return <div style={wrap}><div style={{ ...box, padding: 14, color: "var(--red)", fontSize: 12 }}>{error} <button type="button" onClick={reload} style={{ marginLeft: 8, color: "var(--role)", background: "none", border: "none", cursor: "pointer" }}>Повторить</button></div></div>;
  if (!data) return <div style={wrap} />;

  const s = data.summary;
  const empty = s.views === 0;
  const delayMax = Math.max(1, ...data.delay.buckets.map((b) => b.count));
  const questMax = Math.max(1, ...data.byQuest.map((q) => q.plays));
  const invMax = Math.max(1, ...data.inventory.byQuest.map((q) => q.moments));

  // Строка видео (топ + библиотека) — кликабельна, открывает плеер.
  const videoRow = (v: VideoTopItem, i: number): ReactElement => (
    <button key={v.materialId} type="button" onClick={() => setOpenVideo(v)}
      style={{ display: "grid", gridTemplateColumns: "46px 1fr auto", gap: 8, alignItems: "center", width: "100%", minHeight: 38, padding: "4px 8px", border: "none", borderTop: i ? "1px solid var(--surface-3)" : "none", background: "transparent", color: "var(--text-1)", cursor: "pointer", textAlign: "left" }}>
      <VideoThumb url={v.posterUrl} />
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{v.quest}</span>
        <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>{fmtDm(v.gameDate)} · {v.gameTime}{v.operator ? ` · ${v.operator}` : ""}</span>
      </span>
      <span style={{ ...NUM, fontSize: 11.5, color: "var(--text-2)", textAlign: "right", flex: "0 0 auto" }}>
        <b style={{ color: "var(--text-1)" }}>{fmt(v.plays)}</b> пок · {fmt(v.viewers)} зр
      </span>
    </button>
  );

  const viewerRow = (v: VideoTopViewer): ReactElement => (
    <button key={v.tg} type="button" onClick={() => onImpersonate?.(v.tg)}
      style={{ display: "grid", gridTemplateColumns: "26px 1fr auto auto", gap: 8, alignItems: "center", width: "100%", minHeight: 30, padding: "3px 6px", border: "none", borderTop: "1px solid var(--surface-3)", background: "transparent", color: "var(--text-1)", cursor: onImpersonate ? "pointer" : "default", textAlign: "left" }}>
      <AvatarImg url={v.avatarUrl} name={v.name || v.username} size={26} />
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5 }}>{v.name || (v.username ? "@" + v.username : "id " + v.tg)}</span>
      <span style={{ ...NUM, fontSize: 12, color: "var(--text-2)" }}>{fmt(v.videos)} вид · {fmt(v.watchMin)} мин</span>
      <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>{fmtWhen(v.lastWatch)}</span>
    </button>
  );

  return (
    <div style={wrap}>
      {openVideo ? <VideoModal v={openVideo} from={effFrom} to={effTo} onClose={() => setOpenVideo(null)} /> : null}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={capTitle}>Статистика видео</span>
        <button type="button" onClick={reload} title="Обновить" style={{ display: "inline-grid", placeItems: "center", width: 26, height: 26, borderRadius: 6, border: "1px solid var(--surface-3)", background: "transparent", color: "var(--text-2)", cursor: "pointer" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v5h-5" /></svg>
        </button>
      </div>
      <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={capTitle}>База моментов</span>
          <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>{fmt(data.inventory.totalMoments)} видео · {fmt(data.inventory.gamesWithVideo)} игр</span>
        </div>
        {data.inventory.byQuest.length === 0
          ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>Видео-моментов пока нет</div>
          : data.inventory.byQuest.map((q) => <Bar key={q.quest} label={q.quest} count={q.moments} max={invMax} hint={`· ${fmt(q.games)} игр`} />)}
      </div>

      {empty ? (
        <div style={{ ...box, padding: "10px 12px", color: "var(--text-3)", fontSize: 11.5, lineHeight: 1.45 }}>
          Просмотров за период нет — статистика раскроется с первого просмотра. Полную базу видео смотри во вкладке «Все видео».
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            <Kpi label="Показы" value={fmt(s.views)} sub={`${fmt(s.uniqueViewers)} зрителей`} />
            <Kpi label="Время, мин" value={fmt(s.watchMin)} sub={`ср. ${fmt(s.avgWatchSec)} сек`} />
            <Kpi label="Досмотр" value={`${fmt(s.completionRate)}%`} sub="до конца" />
          </div>
          <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={capTitle}>Когда смотрят после игры</span>
              <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>ср. {fmt(data.delay.avgHours)} ч</span>
            </div>
            {data.delay.buckets.map((b) => <Bar key={b.key} label={b.label} count={b.count} max={delayMax} />)}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 4 }}>
        {(["videos", "viewers", "library"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={seg(tab === t)}>
            {t === "videos" ? "Топ видео" : t === "viewers" ? "Часто смотрят" : "Все видео"}
          </button>
        ))}
      </div>

      {tab === "videos" ? (
        <div style={{ ...box, overflow: "hidden" }}>
          {data.topVideos.length === 0 ? <div style={{ padding: 12, color: "var(--text-3)", fontSize: 12 }}>Просмотров за период нет</div>
            : data.topVideos.map((v, i) => videoRow(v, i))}
        </div>
      ) : tab === "viewers" ? (
        <div style={{ ...box, overflow: "hidden" }}>
          {data.topViewers.length === 0 ? <div style={{ padding: 12, color: "var(--text-3)", fontSize: 12 }}>Просмотров за период нет</div>
            : data.topViewers.map(viewerRow)}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ ...capTitle, fontSize: 9.5, marginRight: 2 }}>Сорт</span>
            {(["date", "game", "quest", "operator"] as const).map((k) => (
              <button key={k} type="button" onClick={() => setLibSort(k)} style={seg(libSort === k)}>
                {k === "date" ? "Дата" : k === "game" ? "Игра" : k === "quest" ? "Квест" : "Оператор"}
              </button>
            ))}
          </div>
          <div style={{ ...box, overflow: "hidden" }}>
            {library.length === 0 ? <div style={{ padding: 12, color: "var(--text-3)", fontSize: 12 }}>За период видео нет</div>
              : library.map((v, i) => videoRow(v, i))}
          </div>
          <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>{fmt(library.length)} видео за период</span>
        </>
      )}

      {!empty && data.byQuest.length > 0 ? (
        <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
          <span style={capTitle}>По квестам</span>
          {data.byQuest.map((q) => <Bar key={q.quest} label={q.quest} count={q.plays} max={questMax} hint={`· ${fmt(q.watchMin)}м`} />)}
        </div>
      ) : null}
    </div>
  );
}
